import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import fx from '../data/fx.json';
import { loadLang, pageLangFor } from '../i18n';
import { numberFormat } from './intl-cache';
import { getGeo } from './personal';

/**
 * The currency switch. Prices are stored and quoted in USD (the marketplace's
 * own checkout shows its own currency — HelloTickets' international storefronts
 * can charge in local currency); everything else is a conversion at the day's rates
 * (scripts/fetch-fx.mjs, refreshed by the nightly build). Converted prices
 * are shown with "≈" and never replace the USD figure where it matters —
 * the comparison table keeps the exact quote under the approximation.
 *
 * Hydration: the prerendered HTML is USD. The saved choice is read after
 * mount, so the first client render matches the server and React never
 * throws the tree away; non-USD visitors see dollars for one frame.
 */
export type Currency = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'MXN';

export const CURRENCIES: { code: Currency; name: string; symbol: string }[] = [
  { code: 'USD', name: 'US dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British pound', symbol: '£' },
  { code: 'CAD', name: 'Canadian dollar', symbol: 'CA$' },
  { code: 'MXN', name: 'Mexican peso', symbol: 'MX$' },
];

/** First-visit default by country (the visitor can always switch). */
export const CURRENCY_BY_COUNTRY: Record<string, Currency> = {
  US: 'USD',
  GB: 'GBP',
  CA: 'CAD',
  MX: 'MXN',
  AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR', FI: 'EUR', FR: 'EUR', GR: 'EUR', HR: 'EUR',
  IE: 'EUR', IT: 'EUR', LT: 'EUR', LU: 'EUR', LV: 'EUR', MC: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SI: 'EUR', SK: 'EUR',
};

const KEY = 'tixinfo.currency';
const CODES = CURRENCIES.map((c) => c.code);
const isCurrency = (v: unknown): v is Currency => typeof v === 'string' && (CODES as string[]).includes(v);

export const RATES: Record<string, number> = fx.rates;
export const RATES_DATE: string | null = fx.date;

export const convert = (usd: number, to: Currency) => (to === 'USD' ? usd : usd * (RATES[to] ?? 1));

/**
 * "$58" / "$70.15" in USD; "≈ €51" / "≈ €62.25" converted. Cents only when
 * there are cents, so round numbers stay round. Converted figures round to
 * whole units above 20 — the approximation sign already says "about", and
 * "≈ €62.25" pretends to a precision the rate doesn't have.
 */
export const formatPrice = (usd: number, currency: Currency = 'USD', opts: { approx?: boolean } = {}) => {
  const value = convert(usd, currency);
  const whole = currency !== 'USD' && value >= 20 ? Math.round(value) : value;
  // one kept formatter per currency and cents choice (src/lib/intl-cache.ts): a board, its daily
  // table and the FAQ print hundreds of prices per render
  const s = numberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: Number.isInteger(whole) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(whole);
  // en-US narrowSymbol gives "$" for CAD and MXN too; a Canadian knows the
  // difference and a German doesn't, so prefix the code's own symbol.
  const sym = currency === 'CAD' ? s.replace(/^\$/, 'CA$') : currency === 'MXN' ? s.replace(/^\$/, 'MX$') : s;
  return currency === 'USD' || opts.approx === false ? sym : `≈ ${sym}`;
};

interface Ctx {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  /** true once the saved choice has been read (so UI can avoid flashing) */
  ready: boolean;
  /** set when the currency was chosen for the visitor from their country —
   *  the note under the header shows until they dismiss it or pick one */
  auto: Currency | null;
  dismissAuto: () => void;
}

const CurrencyContext = createContext<Ctx>({
  currency: 'USD',
  setCurrency: () => {},
  ready: false,
  auto: null,
  dismissAuto: () => {},
});

const NOTED_KEY = 'tixinfo.currency.noted';

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [currency, setState] = useState<Currency>('USD');
  const [ready, setReady] = useState(false);
  const [auto, setAuto] = useState<Currency | null>(null);
  // The visitor chose a currency: a country default that lands later does not override it.
  const picked = useRef(false);

  // Saved choice first. With none, the visitor's country picks the default
  // (a German sees EUR, a Mexican MXN) — never persisted, so a VPN day
  // doesn't stick, and announced in a note they can dismiss. USD visitors
  // and anyone who ever chose manually see nothing.
  useEffect(() => {
    let saved: string | null = null;
    let noted = false;
    try {
      saved = localStorage.getItem(KEY);
      noted = localStorage.getItem(NOTED_KEY) === '1';
    } catch {
      /* no storage */
    }
    if (isCurrency(saved)) {
      setState(saved);
      setReady(true);
      return;
    }
    setReady(true);
    let alive = true;
    getGeo().then(async (g) => {
      const c = g?.country ? CURRENCY_BY_COUNTRY[g.country] : undefined;
      if (!alive || !c || c === 'USD') return;
      // The page's language switches once its dictionary is here (src/i18n loads them on demand):
      // switch the currency with it — the same download, so one render — and the note under the
      // header appears once, already in the visitor's language. A failed download still applies
      // the currency (its note in English).
      const l = pageLangFor(g?.country);
      if (l) await loadLang(l);
      if (!alive || picked.current) return;
      setState(c);
      if (!noted) setAuto(c);
    });
    return () => {
      alive = false;
    };
  }, []);

  const setCurrency = useCallback((c: Currency) => {
    picked.current = true;
    setState(c);
    setAuto(null);
    try {
      localStorage.setItem(KEY, c);
      localStorage.setItem(NOTED_KEY, '1');
    } catch {
      /* private mode: the choice just doesn't persist */
    }
  }, []);

  const dismissAuto = useCallback(() => {
    setAuto(null);
    try {
      localStorage.setItem(NOTED_KEY, '1');
    } catch {
      /* no storage */
    }
  }, []);

  const value = useMemo(
    () => ({ currency, setCurrency, ready, auto, dismissAuto }),
    [currency, setCurrency, ready, auto, dismissAuto]
  );
  return createElement(CurrencyContext.Provider, { value }, children);
};

export const useCurrency = () => useContext(CurrencyContext);

/** A price formatter bound to the visitor's currency: `const price = usePrice(); price(58)`. */
export const usePrice = () => {
  const { currency } = useCurrency();
  return useCallback((usd: number, opts?: { approx?: boolean }) => formatPrice(usd, currency, opts), [currency]);
};

/** Has the visitor made a choice yet? (For the first-visit auto-default.) */
export const hasSavedCurrency = () => {
  try {
    return isCurrency(localStorage.getItem(KEY));
  } catch {
    return false;
  }
};
